import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import { Check, User, Briefcase, ArrowLeft, Ticket, X, CheckCircle2 } from 'lucide-react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Button } from '@/components/ui/atoms/Button';
import { getPaidPlans, getFreePlan, PAID_PACK_CODES, CARD_SAVE_PACK_CODES, getPlanByCode } from '../config/plans';
import { useOnboardingService } from '../services/onboarding.service';
import { createPurchaseSession, validateCoupon, createSetupIntent } from '../services/payment.service';
import { InlineCardInput, type InlineCardHandle } from '../components/payment/InlineCardInput';
import { updateUserProfile, getUserProfile } from '../services/user.service';
import { CurrencyCode, formatPrice } from '../utils/currency';
import { toast } from 'sonner';
import i18n from '@/i18n';
import Header from '../components/layout/Header';
import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  normalizePurchaseStatus,
  isSuccessStatus,
} from '../constants/purchaseSession';
import { generateEventId, fireMetaPixelEvent } from '../services/tracking.service';
import { SegmentedButton } from '../components/ui/atoms/SegmentedButton';

// Currency options
const CURRENCIES: { code: CurrencyCode; label: string; symbol: string }[] = [
  { code: 'USD', label: 'USD', symbol: '' },
  { code: 'EUR', label: 'EUR', symbol: '' },
  { code: 'MAD', label: 'MAD', symbol: '' },
];

// Common job titles for the dropdown
const JOB_TITLES = [
  'Recruiter',
  'HR Manager',
  'HR Director',
  'Talent Acquisition Specialist',
  'Talent Acquisition Manager',
  'Hiring Manager',
  'Head of HR',
  'Chief People Officer',
  'CEO / Founder',
  'CTO',
  'Engineering Manager',
  'Team Lead',
  'Other',
];

export const PackSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const { getMyPendingInvitation, checkFreeTrialStatus, activatePayg, activateGitexTrial } = useOnboardingService();

  // ---------------------------------------------------------------------------
  // Persist form state across page reloads / payment redirects
  // ---------------------------------------------------------------------------
  const STORAGE_KEY = 'invirtus_onboarding_form';

  const loadSavedForm = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const saved = loadSavedForm();

  const preSelectedPack = searchParams.get('pack') || '';
  const gitexVariant = searchParams.get('gitex_variant') || '';
  const choseFreeTrialOnLanding = preSelectedPack === 'free_trial';
  const chosePaidOnLanding = (PAID_PACK_CODES as readonly string[]).includes(preSelectedPack);
  const chosePaygOnLanding = (CARD_SAVE_PACK_CODES as readonly string[]).includes(preSelectedPack);
  const choseGitexOnLanding = preSelectedPack === 'gitex_trial';

  // Determine initial pack selection
  const getInitialPack = () => {
    if (choseFreeTrialOnLanding) return 'free_trial';
    if (chosePaidOnLanding || chosePaygOnLanding || choseGitexOnLanding) return preSelectedPack;
    return saved?.selectedPack || 'starter';
  };

  const [selectedPack, setSelectedPack] = useState<string>(getInitialPack());
  const [currency, setCurrency] = useState<CurrencyCode>(saved?.currency || 'USD');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState<number | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const cardRef = useRef<InlineCardHandle>(null);
  const isMAD = currency === 'MAD';
  // Card input shows for: Gitex with card (non-MAD) and PAYG (non-MAD)
  const needsCard = !isMAD && (
    (selectedPack === 'gitex_trial' && gitexVariant === 'with_card') ||
    selectedPack === 'payg'
  );
  const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

  // Form state — initialised from localStorage when available
  const [firstName, setFirstName] = useState(saved?.firstName || '');
  const [lastName, setLastName] = useState(saved?.lastName || '');
  const [companyName, setCompanyName] = useState(saved?.companyName || '');
  const [phoneNumber, setPhoneNumber] = useState(saved?.phoneNumber || '');
  const [jobTitle, setJobTitle] = useState(saved?.jobTitle || '');
  const [marketingConsent, setMarketingConsent] = useState(saved?.marketingConsent || false);

  // Save form to localStorage on every change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      firstName, lastName, companyName, phoneNumber, jobTitle,
      marketingConsent, selectedPack, currency,
    }));
  }, [firstName, lastName, companyName, phoneNumber, jobTitle, marketingConsent, selectedPack, currency]);

  // Form validation - only required if onboarding not completed
  const isFormValid = onboardingCompleted || (firstName.trim() && lastName.trim() && companyName.trim() && jobTitle);

  // Show payment error toast if redirected from a failed payment
  useEffect(() => {
    if (searchParams.get('payment_error') === '1') {
      toast.error(i18n.t('auth:toasts.payment_processing_failed'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check status on mount
  useEffect(() => {
    const checkStatus = async () => {
      if (authLoading || !user?.email) return;
      
      try {
        const token = await getAccessTokenSilently();
        
        // Check user profile to see if onboarding is already done
        const profileResponse = await getUserProfile(token);
        if (profileResponse.data?.onboarding_completed) {
          setOnboardingCompleted(true);
          localStorage.removeItem(STORAGE_KEY);
          // Pre-fill form with existing data (in case they revisit)
          setFirstName(profileResponse.data.first_name || '');
          setLastName(profileResponse.data.last_name || '');
          setCompanyName(profileResponse.data.company_name || '');
          setPhoneNumber(profileResponse.data.phone_number || '');
          setJobTitle(profileResponse.data.job_title || '');
          setMarketingConsent(profileResponse.data.marketing_consent || false);
        }

        // Check pending invitation
        const pendingInvitation = await getMyPendingInvitation();
        if (pendingInvitation) {
          if (['free_trial', 'starter', 'core', 'pro'].includes(pendingInvitation.pack_code)) {
            setSelectedPack(pendingInvitation.pack_code);
          }
        }

        // Check free trial status
        const freeTrialStatus = await checkFreeTrialStatus();
        setHasUsedFreeTrial(freeTrialStatus.has_used_free_trial);
        
      } catch (err) {
        // Continue with defaults
      } finally {
        setIsLoading(false);
      }
    };
    
    checkStatus();
  }, [authLoading, user, getAccessTokenSilently, getMyPendingInvitation, checkFreeTrialStatus]);

  const handleSubmitAndPurchase = async (packCode: string) => {
    if (!user?.email || !isFormValid) {
      toast.error(i18n.t('common:toasts.fill_required_fields'));
      return;
    }
    
    setIsProcessing(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Update user profile only if onboarding not completed
      if (!onboardingCompleted) {
        const profileResponse = await updateUserProfile(token, {
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          phone_number: phoneNumber || undefined,
          job_title: jobTitle,
          marketing_consent: marketingConsent,
        });

        if (profileResponse.error) {
          throw new Error(profileResponse.error);
        }

        // Track CompleteRegistration after successful profile update
        try {
          const regEventId = generateEventId();
          fireMetaPixelEvent('CompleteRegistration', {}, regEventId);
        } catch {
          // Silent fail
        }
      }

      // Gitex trial without card: activate directly (no payment)
      if (packCode === 'gitex_trial') {
        await activateGitexTrial(false);
        localStorage.removeItem(STORAGE_KEY);
        try {
          const trialEventId = generateEventId();
          fireMetaPixelEvent('StartTrial', {}, trialEventId);
        } catch {
          // Silent fail
        }
        toast.success(i18n.t('auth:toasts.gitex_trial_activated'));
        navigate('/dashboard');
        return;
      }

      // Create purchase session
      const response = await createPurchaseSession(token, {
        pack_code: packCode,
        currency: currency,
        email: user.email,
        coupon_code: couponCode.trim() || undefined,
      });

      if (response.error) {
        throw new Error(response.error);
      }

      const status = normalizePurchaseStatus(response.data?.status ?? null);

      if (isSuccessStatus(status)) {
        localStorage.removeItem(STORAGE_KEY);
        toast.success(i18n.t('auth:toasts.pack_activated'));
        navigate('/dashboard');
        return;
      }

      if (!response.data?.checkout_url) {
        throw new Error('Failed to create checkout session');
      }

      // Form is persisted in localStorage — it will survive the external redirect
      window.location.href = response.data.checkout_url;
    } catch (err: any) {
      console.error('Failed to process:', err);
      const paymentFallback = i18n.t('auth:toasts.payment_processing_failed');
      const message = typeof err?.message === 'string' ? err.message.trim() : '';
      toast.error(
        message && message !== i18n.t('auth:toasts.payment_processing_failed', { lng: 'en' })
          ? message
          : paymentFallback,
      );
      setIsProcessing(false);
    }
  };

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponValidating(true);
    setCouponError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await validateCoupon(token, couponCode.trim(), user?.email);
      if (res.data?.valid) {
        setCouponDiscount(res.data.discount_percent);
        setCouponError(null);
      } else {
        setCouponDiscount(null);
        setCouponError(res.data?.error || 'Invalid coupon');
      }
    } catch {
      setCouponDiscount(null);
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponDiscount(null);
    setCouponError(null);
    setCouponOpen(false);
  };

  const handlePurchase = () => {
    if (selectedPack === 'free_trial') {
      if (hasUsedFreeTrial) {
        toast.error(i18n.t('auth:toasts.trial_already_used'));
        return;
      }
    }

    // Plans that need inline card save (Gitex with card on non-MAD, or PAYG)
    if (needsCard) {
      if (!onboardingCompleted && !isFormValid) {
        toast.error(i18n.t('common:toasts.fill_required_fields'));
        return;
      }
      if (!cardRef.current) {
        toast.error(i18n.t('auth:toasts.card_form_not_ready'));
        return;
      }
      (async () => {
        setIsProcessing(true);
        try {
          const token = await getAccessTokenSilently();
          const planCode = selectedPack === 'gitex_trial' ? 'gitex_trial' : 'payg';

          // 1. Save profile + create invitation (creates tenant)
          if (!onboardingCompleted && user?.email) {
            await updateUserProfile(token, {
              first_name: firstName, last_name: lastName,
              company_name: companyName, phone_number: phoneNumber || undefined,
              job_title: jobTitle, marketing_consent: marketingConsent,
            });
            await fetch(`${import.meta.env.VITE_API_SERVER_URL}/api/onboarding/self-service-invitation`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: user.email, pack_code: planCode }),
            });
          }

          // 2. Create SetupIntent (now tenant exists)
          const setupRes = await createSetupIntent(token);
          if (!setupRes.data?.client_secret) {
            throw new Error('Failed to create card setup');
          }

          // 3. Confirm card with Stripe
          const cardResult = await cardRef.current!.confirmCard(setupRes.data.client_secret);
          if (!cardResult.success) {
            throw new Error(cardResult.error || 'Card verification failed');
          }

          // 4. Activate plan
          if (selectedPack === 'gitex_trial') {
            await activateGitexTrial(true);
            toast.success(i18n.t('auth:toasts.gitex_trial_activated'));
            try { fireMetaPixelEvent('StartTrial', {}, generateEventId()); } catch {}
          } else {
            await activatePayg();
            toast.success(i18n.t('auth:toasts.payg_activated'));
            try { fireMetaPixelEvent('Subscribe', {}, generateEventId()); } catch {}
          }
          localStorage.removeItem(STORAGE_KEY);
          navigate('/dashboard');
        } catch (err: any) {
          const activateFallback = i18n.t('settings:billing_selector.activate_failed');
          const message = typeof err?.message === 'string' ? err.message.trim() : '';
          toast.error(
            message && message !== i18n.t('settings:billing_selector.activate_failed', { lng: 'en' })
              ? message
              : activateFallback,
          );
        } finally {
          setIsProcessing(false);
        }
      })();
      return;
    }

    // Gitex without card (or Gitex + MAD): activate directly
    if (selectedPack === 'gitex_trial') {
      handleSubmitAndPurchase(selectedPack);
      return;
    }

    handleSubmitAndPurchase(selectedPack);
  };

  const paidPlans = getPaidPlans();
  const freePlanConfig = getFreePlan();
  const paygPlanConfig = getPlanByCode('payg');
  const gitexPlanConfig = getPlanByCode('gitex_trial');

  const formattedPrices = useMemo(() => {
    const all = [
      ...paidPlans,
      ...(freePlanConfig ? [freePlanConfig] : []),
      ...(paygPlanConfig ? [paygPlanConfig] : []),
      ...(gitexPlanConfig ? [gitexPlanConfig] : []),
    ];
    return all.reduce<Record<string, string>>((acc, plan) => {
      acc[plan.packCode] = formatPrice(plan.baseAmount, currency, plan.eurAmount);
      return acc;
    }, {});
  }, [currency, paidPlans, freePlanConfig, paygPlanConfig, gitexPlanConfig]);

  // Per-pack savings vs Pay as you go ($15/interview)
  const GITEX_DISCOUNTS: Record<string, number> = { starter: 5, core: 10, pro: 15 };
  // What each pack would cost if paid at PAYG rate ($15 x interviews)
  const PAYG_EQUIVALENTS: Record<string, number> = { starter: 300, core: 1500, pro: 3000 };

  // Build the list of available plans for tabs — order matches landing page
  // PAYG disabled for MAD (no card saving with Berexia)
  const showFreeTrialTab = !hasUsedFreeTrial && !onboardingCompleted && !!freePlanConfig;
  const showGitexTab = choseGitexOnLanding && !!gitexPlanConfig;
  const showPaygTab = !isMAD && !!paygPlanConfig;
  const availablePlans = [
    ...(showFreeTrialTab && !showGitexTab ? [freePlanConfig!] : []),
    ...(showGitexTab ? [gitexPlanConfig!] : []),
    ...(showPaygTab ? [paygPlanConfig!] : []),
    ...paidPlans,
  ];

  // Get selected plan config
  const selectedPlan = availablePlans.find(p => p.packCode === selectedPack) || paidPlans[0];

  // If PAYG selected but MAD chosen, switch away
  useEffect(() => {
    if (selectedPack === 'payg' && isMAD) {
      setSelectedPack('starter');
    }
  }, [selectedPack, isMAD]);

  // If selected pack is free_trial but it's not available, fall back to first paid plan
  useEffect(() => {
    if (selectedPack === 'free_trial' && !showFreeTrialTab) {
      setSelectedPack('starter');
    }
  }, [selectedPack, showFreeTrialTab]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-gray">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple mx-auto"></div>
          <p className="mt-4 text-dark-invirtus/70 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Render the onboarding split layout for first-time users
  const renderOnboardingLayout = () => (
    <div className="max-w-6xl w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        
        {/* Left: Profile Form Card */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-[20px] p-6 lg:p-8 shadow-sm border border-light-lila"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-gradient-purple/20 flex items-center justify-center">
              <User className="w-5 h-5 text-dark-invirtus" />
            </div>
            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-dark-invirtus">
              Tell us about yourself
            </h2>
          </div>
          <div className="mb-6" />

          <div className="space-y-5">
            {/* First Name & Last Name Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-dark-invirtus font-medium">
                  First Name <span className="text-red">*</span>
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-dark-invirtus font-medium">
                  Last Name <span className="text-red">*</span>
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            {/* Company Name — used to create the user's tenant (workspace) */}
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-dark-invirtus font-medium">
                Company Name <span className="text-red">*</span>
              </Label>
              <Input
                id="companyName"
                name="organization"
                autoComplete="organization"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              <p className="text-xs text-dark-invirtus/70">
                This becomes your workspace in Invirtus.
              </p>
            </div>

            {/* Phone Number with Country Code */}
            <div className="space-y-2">
              <Label htmlFor="phoneNumber" className="text-dark-invirtus font-medium">
                Phone Number
              </Label>
              <div className="phone-input-wrapper">
                <PhoneInput
                  defaultCountry="us"
                  value={phoneNumber}
                  onChange={(phone) => setPhoneNumber(phone)}
                  inputProps={{
                    name: 'phone',
                    autoComplete: 'tel',
                  }}
                />
              </div>
            </div>

            {/* Job Title Select */}
            <div className="space-y-2">
              <Label htmlFor="jobTitle" className="text-dark-invirtus font-medium">
                Job Title <span className="text-red">*</span>
              </Label>
              <Select value={jobTitle} onValueChange={setJobTitle}>
                <SelectTrigger className="h-11 border-light-lila focus:border-purple focus:ring-purple">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_TITLES.map((title) => (
                    <SelectItem key={title} value={title}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Card details — inline, only for Gitex with card */}
            {needsCard && stripePublishableKey && (
              <div className="space-y-2">
                <Label className="text-dark-invirtus font-medium">
                  Card Details
                </Label>
                <div className="px-3 py-3 rounded-lg border border-light-lila focus-within:border-purple focus-within:ring-1 focus-within:ring-purple [&_iframe]{min-h-[20px]}">
                  <InlineCardInput ref={cardRef} publishableKey={stripePublishableKey} />
                </div>
                <p className="text-xs text-dark-invirtus/50">
                  Saved securely for future use. No charge today.
                </p>
              </div>
            )}

            {/* Marketing Consent Checkbox */}
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="marketingConsent"
                checked={marketingConsent}
                onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                className="mt-0.5 border-light-lila data-[state=checked]:bg-purple data-[state=checked]:border-purple"
              />
              <Label 
                htmlFor="marketingConsent" 
                className="text-sm text-dark-invirtus/70 font-normal leading-relaxed cursor-pointer"
              >
                I agree to receive emails from Invirtus about new products, features, and updates. You can unsubscribe at any time.
              </Label>
            </div>
          </div>
        </motion.div>

        {/* Right: Selected Plan Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col"
        >
          {/* Header with title and currency selector in same row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-purple/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-dark-invirtus" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-dark-invirtus">
                Your Selected Plan
              </h2>
            </div>
            {/* Currency Selector - horizontal toggle */}
            <SegmentedButton
              aria-label="Currency"
              size="sm"
              appearance="raised"
              intent="secondary"
              value={currency}
              onValueChange={(next) => setCurrency(next as CurrencyCode)}
              options={CURRENCIES.map((curr) => ({
                value: curr.code,
                label: curr.label,
              }))}
            />
          </div>

          {/* Plan selector */}
          <SegmentedButton
            aria-label="Plan"
            className="mb-4"
            size="md"
            appearance="filled"
            intent="secondary"
            equalWidth
            value={selectedPack}
            onValueChange={setSelectedPack}
            options={availablePlans.map((plan) => ({
              value: plan.packCode,
              label: plan.planName,
            }))}
          />

          {/* Selected Plan Details */}
          <div className="flex-1 bg-[linear-gradient(150deg,var(--color-light-red)_0%,var(--color-light-lin)_40%,var(--color-gradient-light)_50%,var(--color-gradient-purple)_100%)] rounded-[20px] p-6 lg:p-7">
            <div className="flex flex-col h-full">
              <h3 className="text-2xl font-bold text-dark-invirtus mb-2">
                {selectedPlan.planName}
              </h3>

              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl lg:text-5xl font-bold text-dark-invirtus">
                  {formattedPrices[selectedPlan.packCode]}
                </span>
                {/* Show PAYG equivalent crossed out for packs */}
                {PAYG_EQUIVALENTS[selectedPlan.packCode] && (
                  <span className="text-lg text-dark-invirtus/35 line-through">
                    {formatPrice(PAYG_EQUIVALENTS[selectedPlan.packCode], currency)}
                  </span>
                )}
                {selectedPlan.priceSubtext && (
                  <span className="text-lg text-dark-invirtus">
                    {selectedPlan.priceSubtext}
                  </span>
                )}
              </div>

              {GITEX_DISCOUNTS[selectedPlan.packCode] && (
                <p className="text-sm text-dark-invirtus/60 mb-1">
                  Save {GITEX_DISCOUNTS[selectedPlan.packCode]}% vs Pay as you go
                </p>
              )}

              <p className="text-dark-invirtus font-medium mb-6">
                {selectedPlan.description}
              </p>

              {/* Features List */}
              <div className="flex-1 mb-6">
                <div className="space-y-2.5">
                  {selectedPlan.features?.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-dark-invirtus" />
                      <span className="text-sm text-dark-invirtus">
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coupon — for all paid plans */}
              {selectedPack !== 'free_trial' && selectedPack !== 'gitex_trial' && (
                <div className="mb-4">
                  {couponDiscount !== null ? (
                    /* Coupon applied — success state */
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-green-800">
                            {couponCode.toUpperCase()} — {couponDiscount}% off
                          </p>
                        </div>
                      </div>
                      <Button
                        type="icon"
                        intent="muted"
                        size="xs"
                        onClick={handleRemoveCoupon}
                        aria-label="Remove coupon"
                        icon={<X />}
                      />
                    </div>
                  ) : couponOpen ? (
                    /* Coupon input expanded */
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          type="text"
                          value={couponCode}
                          onChange={(e) => { setCouponCode(e.target.value); setCouponError(null); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                          placeholder="Enter coupon code"
                          autoFocus
                          size="sm"
                          error={couponError ?? undefined}
                        />
                      </div>
                        <Button
                          label="Apply"
                          type="contained"
                          intent="secondary"
                          size="xs"
                          onClick={handleApplyCoupon}
                          disabled={!couponCode.trim()}
                          loading={couponValidating}
                        />
                        <Button
                          type="icon"
                          intent="muted"
                          size="xs"
                          onClick={() => { setCouponOpen(false); setCouponCode(''); setCouponError(null); }}
                          aria-label="Close coupon"
                          icon={<X />}
                        />
                    </div>
                  ) : (
                    /* Collapsed — trigger aligned right */
                    <div className="flex justify-end">
                      <Button
                        label="Apply coupon"
                        type="text"
                        intent="secondary"
                        size="sm"
                        icon={<Ticket/>}
                        onClick={() => setCouponOpen(true)}
                      />
                    </div>
                  )}
                </div>
              )}

              <Button
                label={`Get ${selectedPlan.planName}`}
                type="contained"
                intent="secondary"
                size="md"
                onClick={handlePurchase}
                disabled={!isFormValid}
                loading={isProcessing}
                className="w-full"
              />

              {!isFormValid && (
                <p className="text-sm text-dark-invirtus/60 text-center mt-3">
                  Please fill in the required fields on the left
                </p>
              )}
            </div>
          </div>

        </motion.div>

      </div>
    </div>
  );

  // Plan-only layout — profile already saved, just need to pick/pay a plan
  const renderPlanOnlyLayout = () => (
    <div className="max-w-lg w-full">
      {onboardingCompleted && (
        <Button
          type="text"
          intent="muted"
          size="sm"
          className="mb-4"
          icon={<ArrowLeft />}
          label="Back to Dashboard"
          onClick={() => navigate('/dashboard')}
        />
      )}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col"
      >
        {/* Header with title and currency selector */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-purple/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-dark-invirtus" />
            </div>
            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-dark-invirtus">
              Choose Your Plan
            </h2>
          </div>
          <SegmentedButton
            aria-label="Currency"
            size="sm"
            appearance="raised"
            intent="secondary"
            value={currency}
            onValueChange={(next) => setCurrency(next as CurrencyCode)}
            options={CURRENCIES.map((curr) => ({
              value: curr.code,
              label: curr.label,
            }))}
          />
        </div>

        {/* Plan selector */}
        <SegmentedButton
          aria-label="Plan"
          className="mb-4"
          size="md"
          appearance="filled"
          intent="secondary"
          equalWidth
          value={selectedPack}
          onValueChange={setSelectedPack}
          options={availablePlans.map((plan) => ({
            value: plan.packCode,
            label: plan.planName,
          }))}
        />

        {/* Selected Plan Details */}
        <div className="bg-[linear-gradient(150deg,var(--color-light-red)_0%,var(--color-light-lin)_40%,var(--color-gradient-light)_50%,var(--color-gradient-purple)_100%)] rounded-[20px] p-6 lg:p-7">
          <div className="flex flex-col">
            <h3 className="text-2xl font-bold text-dark-invirtus mb-2">
              {selectedPlan.planName}
            </h3>

            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-4xl lg:text-5xl font-bold text-dark-invirtus">
                {formattedPrices[selectedPlan.packCode]}
              </span>
              {PAYG_EQUIVALENTS[selectedPlan.packCode] && (
                <span className="text-lg text-dark-invirtus/35 line-through">
                  {formatPrice(PAYG_EQUIVALENTS[selectedPlan.packCode], currency)}
                </span>
              )}
              {selectedPlan.priceSubtext && (
                <span className="text-lg text-dark-invirtus">
                  {selectedPlan.priceSubtext}
                </span>
              )}
            </div>

            {GITEX_DISCOUNTS[selectedPlan.packCode] && (
              <p className="text-sm text-dark-invirtus/60 mb-1">
                Save {GITEX_DISCOUNTS[selectedPlan.packCode]}% vs Pay as you go
              </p>
            )}

            <p className="text-dark-invirtus font-medium mb-6">
              {selectedPlan.description}
            </p>

            <div className="mb-6 space-y-2.5">
              {selectedPlan.features?.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-dark-invirtus" />
                  <span className="text-sm text-dark-invirtus">{feature.text}</span>
                </div>
              ))}
            </div>

            {needsCard && stripePublishableKey && (
              <div className="mb-4 space-y-2">
                <Label className="text-dark-invirtus font-medium">
                  Card Details
                </Label>
                <div className="px-3 py-3 rounded-lg border border-light-lila bg-white focus-within:border-purple focus-within:ring-1 focus-within:ring-purple [&_iframe]{min-h-[20px]}">
                  <InlineCardInput ref={cardRef} publishableKey={stripePublishableKey} />
                </div>
                <p className="text-xs text-dark-invirtus/50">
                  Saved securely for future use. No charge today.
                </p>
              </div>
            )}

            {/* Coupon — for paid plans */}
            {selectedPack !== 'free_trial' && selectedPack !== 'gitex_trial' && (
              <div className="mb-4">
                {couponDiscount !== null ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-green-800">
                          {couponCode.toUpperCase()} — {couponDiscount}% off
                        </p>
                      </div>
                    </div>
                    <Button
                      type="icon"
                      intent="muted"
                      size="xs"
                      onClick={handleRemoveCoupon}
                      aria-label="Remove coupon"
                      icon={<X />}
                    />
                  </div>
                ) : couponOpen ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value); setCouponError(null); }}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                        placeholder="Enter coupon code"
                        autoFocus
                        size="sm"
                        error={couponError ?? undefined}
                      />
                    </div>
                      <Button
                        label="Apply"
                        type="contained"
                        intent="secondary"
                        size="xs"
                        onClick={handleApplyCoupon}
                        disabled={!couponCode.trim()}
                        loading={couponValidating}
                      />
                      <Button
                        type="icon"
                        intent="muted"
                        size="xs"
                        onClick={() => { setCouponOpen(false); setCouponCode(''); setCouponError(null); }}
                        aria-label="Close coupon"
                        icon={<X />}
                      />
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button
                      label="Apply coupon"
                      type="text"
                      intent="secondary"
                      size="sm"
                      icon={<Ticket/>}
                      onClick={() => setCouponOpen(true)}
                    />
                  </div>
                )}
              </div>
            )}

            <Button
              label={`Get ${selectedPlan.planName}`}
              type="contained"
              intent="secondary"
              size="md"
              onClick={handlePurchase}
              loading={isProcessing}
              className="w-full"
            />
          </div>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-gray flex flex-col font-body">
      <Header />
      
      <main className="flex-grow flex items-center justify-center px-4 py-8 lg:py-12">
        {onboardingCompleted ? renderPlanOnlyLayout() : renderOnboardingLayout()}
      </main>
    </div>
  );
};

export default PackSelectionPage;
